// ===============================================================
// ✅ AuthContext.js — FINAL (Presence via RTDB only)
// - NO Firestore writes for presence (online/inApp/lastSeen)
// - PresenceGate handles RTDB presence
// - Server syncPresenceToFirestore updates Firestore
// - Logout uses forceLogout callable then signOut
// ===============================================================

import React, { createContext, useState, useEffect } from "react";
import { auth } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { registerCurrentDevice } from "../services/deviceSecurity";
import { clearDeviceSessionId } from "../services/deviceSession";
import {
  registerFcmForCurrentUser,
  unregisterFcmForCurrentUser,
  startFcmTokenRefreshListener,
} from "../services/fcmRegistration";
import {
  bootstrapNativeFirebaseSession,
  clearNativeFirebaseSession,
} from "../plugins/NativeFirebaseSession";
import { clearShellState } from "../plugins/NativeBottomNav";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  // ✅ callable: forceLogout (server only)
  const functions = getFunctions(undefined, "us-central1");
  const forceLogoutFn = httpsCallable(functions, "forceLogout");

  // ===========================
  // 👤 متابعة تسجيل الدخول والخروج (بدون Firestore)
  // ===========================
  useEffect(() => {
    // Started once per app lifetime, independent of auth state - it safely
    // no-ops on a rotation event while logged out (see fcmRegistration.js).
    void startFcmTokenRefreshListener();

    const unsub = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || null);
      setIsReady(true);

      if (!currentUser?.uid) {
        // Fires for BOTH logout paths (AuthContext.safeLogout AND
        // SingleSessionGate's own direct signOut(auth) call) since this
        // callback is driven by real Firebase Auth state, not by which
        // JS function initiated the change - best-effort, mirrors
        // clearPendingNativeNotificationHandoff's own never-throw
        // contract. Fires synchronously here (before any React re-render/
        // effect pass) so the native bar hides as early as possible, not
        // merely as a side effect of NativeShellController's own later
        // reactive [isReady, uid] effect (which still covers the same
        // case as a second, independent layer).
        void clearShellState();
      }

      if (currentUser?.uid) {
        void registerCurrentDevice();
        void registerFcmForCurrentUser(currentUser);
        // No-op on non-Android platforms. Runs once per login/account
        // switch so every native page (not just the Notification Center)
        // can rely on a matching native FirebaseAuth session without
        // touching the WebView again - see NativeFirebaseSession.js.
        void bootstrapNativeFirebaseSession(currentUser);
      }
    });

    return () => unsub();
  }, []);

  // ===========================
  // 🚪 تسجيل خروج صحيح وآمن (Server + Client)
  // ===========================
  const safeLogout = async () => {
    clearDeviceSessionId();

    try {
      // ✅ 1) أجبره يصير Offline على السيرفر + RTDB
      await forceLogoutFn();
    } catch (e) {
      // حتى لو فشل (انترنت/صلاحيات)، نكمل signOut
      console.warn("⚠️ forceLogout failed (continue):", e?.message || e);
    }

    try {
      await unregisterFcmForCurrentUser();
    } catch (e) {
      console.warn("⚠️ unregisterFcm failed (continue):", e?.message || e);
    }

    try {
      const result = await clearNativeFirebaseSession();
      if (result && result.cleared !== true && result.status !== "not_applicable" && result.status !== "plugin_not_present") {
        // Do not silently treat this as success - the native session may
        // still hold the old account. NotificationCenterActivity's own
        // AuthStateListener is the second line of defense: it independently
        // detects a null/mismatched native uid and closes/clears itself
        // even if this JS-driven clear failed.
        console.warn("⚠️ native FirebaseAuth session did not confirm clearing:", result.status);
      }
    } catch (e) {
      console.warn("⚠️ clearNativeFirebaseSession failed (continue):", e?.message || e);
    }

    // ✅ 2) signOut محلي
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, isReady, safeLogout }}>
      {children}
    </AuthContext.Provider>
  );
};